// PARA-105 §9 — i18n-поток: bundled-словари 4 языков, определение локали при
// первом запуске (AC-6), хранение ручного выбора в localStorage (AC-7).
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import WebApp from '@twa-dev/sdk';

import beAuth from './be/auth.json';
import beBilling from './be/billing.json';
import beCommon from './be/common.json';
import beFaq from './be/faq.json';
import beProcrastination from './be/procrastination.json';
import beReports from './be/reports.json';
import beSettings from './be/settings.json';
import beTasks from './be/tasks.json';
import beVoice from './be/voice.json';
import deAuth from './de/auth.json';
import deBilling from './de/billing.json';
import deCommon from './de/common.json';
import deFaq from './de/faq.json';
import deProcrastination from './de/procrastination.json';
import deReports from './de/reports.json';
import deSettings from './de/settings.json';
import deTasks from './de/tasks.json';
import deVoice from './de/voice.json';
import enAuth from './en/auth.json';
import enBilling from './en/billing.json';
import enCommon from './en/common.json';
import enFaq from './en/faq.json';
import enProcrastination from './en/procrastination.json';
import enReports from './en/reports.json';
import enSettings from './en/settings.json';
import enTasks from './en/tasks.json';
import enVoice from './en/voice.json';
import ruAuth from './ru/auth.json';
import ruBilling from './ru/billing.json';
import ruCommon from './ru/common.json';
import ruFaq from './ru/faq.json';
import ruProcrastination from './ru/procrastination.json';
import ruReports from './ru/reports.json';
import ruSettings from './ru/settings.json';
import ruTasks from './ru/tasks.json';
import ruVoice from './ru/voice.json';

export const SUPPORTED_LOCALES = ['ru', 'en', 'be', 'de'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const FALLBACK_LOCALE: SupportedLocale = 'en';

export const LOCALE_STORAGE_KEY = 'paraparapam:locale';

function isSupportedLocale(value: string | undefined): value is SupportedLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function getStoredLocale(): SupportedLocale | null {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY) ?? undefined;
  return isSupportedLocale(stored) ? stored : null;
}

/** Временная локаль до ответа backend (§9 п.1): сохранённый выбор, иначе
 * грубая догадка из Telegram initDataUnsafe.user.language_code, иначе fallback. */
export function guessInitialLocale(): SupportedLocale {
  const stored = getStoredLocale();
  if (stored) {
    return stored;
  }
  const telegramLocale = WebApp.initDataUnsafe?.user?.language_code;
  if (isSupportedLocale(telegramLocale)) {
    return telegramLocale;
  }
  return FALLBACK_LOCALE;
}

void i18n.use(initReactI18next).init({
  resources: {
    ru: {
      common: ruCommon,
      auth: ruAuth,
      tasks: ruTasks,
      procrastination: ruProcrastination,
      reports: ruReports,
      billing: ruBilling,
      voice: ruVoice,
      faq: ruFaq,
      settings: ruSettings,
    },
    en: {
      common: enCommon,
      auth: enAuth,
      tasks: enTasks,
      procrastination: enProcrastination,
      reports: enReports,
      billing: enBilling,
      voice: enVoice,
      faq: enFaq,
      settings: enSettings,
    },
    be: {
      common: beCommon,
      auth: beAuth,
      tasks: beTasks,
      procrastination: beProcrastination,
      reports: beReports,
      billing: beBilling,
      voice: beVoice,
      faq: beFaq,
      settings: beSettings,
    },
    de: {
      common: deCommon,
      auth: deAuth,
      tasks: deTasks,
      procrastination: deProcrastination,
      reports: deReports,
      billing: deBilling,
      voice: deVoice,
      faq: deFaq,
      settings: deSettings,
    },
  },
  lng: guessInitialLocale(),
  fallbackLng: FALLBACK_LOCALE,
  defaultNS: 'common',
  ns: [
    'common',
    'auth',
    'tasks',
    'procrastination',
    'reports',
    'billing',
    'voice',
    'faq',
    'settings',
  ],
  interpolation: { escapeValue: false },
});

/** Применить локаль: синхронно переключить i18next, опционально сохранить
 * как явный пользовательский выбор (AC-7). */
export function applyLocale(locale: SupportedLocale, persist: boolean): void {
  void i18n.changeLanguage(locale);
  if (persist) {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
}

/** §9 п.2: backend-локаль становится дефолтом ровно один раз — только если
 * пользователь ещё не сделал явный выбор. */
export function applyBackendLocaleIfNoManualChoice(locale: string): void {
  if (getStoredLocale() !== null || !isSupportedLocale(locale)) {
    return;
  }
  applyLocale(locale, true);
}

// PARA-107, AC-2/AC-3/AC-5: активный список локалей приходит с backend при
// каждом логине; до первого ответа считаем активными все 4 поддерживаемых.
let _activeLocales: SupportedLocale[] = [...SUPPORTED_LOCALES];

/** Текущий активный (администратором не деактивированный) список локалей. */
export function getActiveLocales(): SupportedLocale[] {
  return _activeLocales;
}

/** PARA-107, AC-2/AC-3/AC-3a: зафиксировать активный список локалей после
 * логина; если текущий отображаемый язык деактивирован — transient-fallback
 * на FALLBACK_LOCALE (persist=false, localStorage НЕ трогается). Проверка
 * через .includes() — порядок массива не гарантирован (EC-5). */
export function enforceActiveLocale(supportedLocales: SupportedLocale[]): void {
  _activeLocales = supportedLocales;
  if (!supportedLocales.includes(i18n.language as SupportedLocale)) {
    applyLocale(FALLBACK_LOCALE, false);
  }
}

export default i18n;
